/**
 * IMPLEMENT-14 (Juni 2026) — Lehrbild-Overlay (read-only).
 *
 * Lädt für die aktuell betrachtete Welt das KI-Bild-Manifest (normalisierter
 * Quelltext -> Bild-URL) und stellt es den bestehenden VSI-Renderern bereit.
 *
 * STRENG ADDITIV & beweisneutral: greift NUR auf das isolierte Bild-Manifest zu,
 * KEINE Berührung von D1/Course/Evidence/WORM/content_hash/PDF. Fehlt ein Bild,
 * rendern die Kacheln unverändert weiter (fail-closed).
 *
 * BLOCK 3D · Sub-Schritt C (Juli 2026) — additive Erweiterung:
 * `startLehrbildManifestPolling(worldId, expectedKeys, opts)` lädt das Manifest
 * für eine noch unvollständige LP3-Welt-Vorschau in festen Intervallen erneut.
 * Genau EIN Poller pro `worldId`, endet automatisch bei Timeout, komplett
 * aufgelöstem Manifest, Unmount oder Weltwechsel. Keine WebSockets, kein SSE,
 * kein Direkt-S3, kein neues Manifestformat.
 *
 * BLOCK 3D · Refresh-Fix (2026-07-14) — zentrale Manifest-Aktualisierung.
 * Das Manifest wird im Backend nachweislich EINMAL am Ende des Bildjobs
 * geschrieben (siehe `preview_images.py:975`, `_write_welt_manifest`).
 * Solange die Datei nicht existiert, liefert der Endpoint
 * `{images:{}, count:0}` ohne `updated_at`. Sobald das Manifest terminal ist,
 * enthält die Response ein `updated_at`. Diese binäre Kante nutzt die
 * gemeinsame Runtime als einziges Abschluss-Signal: `setActiveLehrbildWorld`
 * startet nach dem initialen Fetch einen zentralen Poll (max. 1 pro `worldId`)
 * genau dann, wenn `updated_at` fehlt. Der Poll stoppt beim ersten
 * `updated_at != null` oder nach `POLL_TIMEOUT_MS`. Dadurch erscheinen später
 * fertiggestellte Lehrbilder automatisch in allen Konsumenten (`Welt.jsx`,
 * `MonteurAufgabe.jsx`, `MonteurWelt.jsx`, `WeltPlakat.jsx`, …), ohne dass
 * Consumer geändert werden müssen. `expectedKeys` bleibt optional für alte
 * Aufrufer (z. B. `WeltDokument.jsx`), ist aber unter der bewiesenen
 * Backend-Schreibweise nicht mehr das primäre Abschlusskriterium.
 */
import { useSyncExternalStore } from "react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const _manifests = {}; // worldId -> { [normKey]: url }
// BLOCK 3D · Refresh-Fix · Manifest-Metadaten je Welt (separat gehalten, damit
// `useLehrbildImage`/`LehrbildImage` byte-identisch bleiben und kein Consumer
// eine neue Struktur konsumieren muss). Enthält den Abschluss-Marker
// `updated_at` sowie den vom Endpoint gelieferten `count`.
const _manifestMeta = {}; // worldId -> { updated_at: string|null, count: number }
// IP2-D1C · Mobile-Derivate je Welt (additives Manifestfeld aus IP2-D1B).
// Separat gehalten, damit `_manifests` und alle bestehenden Consumer
// byte-identisch bleiben. Wird NUR im Monteur-Kontext ausgewertet.
const _mobileDerivatives = {}; // worldId -> { [normKey]: url }
// IP2-E1D · Web-Derivate je Welt (additives Manifestfeld aus IP2-E1B).
// Reines Runtime-Einlesen bereits vorhandener Manifestdaten (KEINE Fachlogik,
// Enterprise-Regel: Transport → Runtime → Auswahl). Separat gehalten, damit
// `_manifests` und alle bestehenden Consumer byte-identisch bleiben.
// Wird NUR im Desktop-Kontext ausgewertet.
const _webDerivatives = {}; // worldId -> { [normKey]: url }
// IP2-D2 · Print-Derivate je Welt (additives Manifestfeld aus IP2-D2A).
// Exakt dasselbe Muster wie mobile/web: reines Runtime-Einlesen, separat
// gehalten, byte-identische Bestands-Consumer. Wird NUR im Print-Kontext
// ausgewertet.
const _printDerivatives = {}; // worldId -> { [normKey]: url }
// IP2-D1C · enger Renderkontext: AUSSCHLIESSLICH MonteurWelt.jsx und
// MonteurAufgabe.jsx setzen ihn. Owner/Desktop/Plakat/Landing/Druck/PDF
// setzen ihn nie und behalten byte-identisches Verhalten (immer Master).
let _monteurKontext = false;
// IP2-E1C · enger Desktop-/Owner-Renderkontext (boolean-only): AUSSCHLIESSLICH
// die interaktive Owner-Welt (`Welt.jsx`, NUR bei `!druckansicht`) setzt ihn.
// Browserdruck/Chromium-PDF (druckansicht), Plakat, Landing, Unterweisung und
// Monteur setzen ihn NIE. In dieser Stufe wird der Kontext AUSSCHLIESSLICH
// vorbereitet: KEINE Bildauswahl, KEINE Nutzung von `web_derivatives` —
// `useLehrbildImage` liest ihn noch nicht (Umschaltpunkt folgt erst in E1D).
// PRIORITÄTSVERTRAG (verbindlich für kommende Stufen, hier NUR dokumentiert,
// NICHT angewendet): Monteur > Desktop > Master.
let _desktopKontext = false;
// IP2-D2 · enger Print-Renderkontext (boolean-only): AUSSCHLIESSLICH die
// Druckprodukte setzen ihn — `Welt.jsx` NUR bei `druckansicht`
// (Einmalkauf-PDF/Arbeitsschutzmappe via Chromium), `WeltPlakat.jsx`
// (Unterweisungsplakat, Browserdruck) und `UnterweisungsnachweisDruck.jsx`
// (Chromium). Landing, Monteur, Owner-Web und Browser-Preview setzen ihn NIE.
// PRIORITÄTSVERTRAG (verbindlich, IP2-D2 Betreiberentscheidung):
// Monteur → Mobile · Desktop → Web · Print → Print · Sonst → Master.
let _printKontext = false;
const _loaded = {}; // worldId -> bool
let _activeWorld = null;
let _version = 0;
const _listeners = new Set();

// BLOCK 3D · Sub-Schritt C · aktive Polls pro worldId (max. 1 Poll je Welt).
const _polls = {}; // worldId -> { stop, id, startedAt, expected }

// BLOCK 3D · Refresh-Fix · zentrale, testbare Poll-Parameter.
// BLOCK 3E · Adaptive Polling-Zeitsteuerung (2026-02).
// Reale Runtime-Messungen (auto-*_meta.json) zeigen: Welten mit bis zu 30
// Lehrbildern erreichen Gesamtlaufzeiten bis ~8 Minuten. Deshalb wird das
// Polling in drei Phasen aufgeteilt (schnell → mittel → langsam), damit
// kurze Jobs weiterhin nahezu sofort erscheinen und lange Jobs die Backend-
// Last deutlich senken. Der Timeout wird auf 12 Min angehoben (Reserve).
// Aufrufer, die ein explizites `intervalMs` in `opts` übergeben (z. B.
// `WeltDokument.jsx`), behalten das feste Intervall (Rückwärtskompatibilität).
export const POLL_INTERVAL_MS = 3000;             // Phase 1 · 0–30 s
export const POLL_PHASE_2_INTERVAL_MS = 5000;     // Phase 2 · 30 s–2 min
export const POLL_PHASE_3_INTERVAL_MS = 10000;    // Phase 3 · ab 2 min
export const POLL_PHASE_2_START_MS = 30000;
export const POLL_PHASE_3_START_MS = 120000;
export const POLL_TIMEOUT_MS = 720000;            // 12 Min

// BLOCK 3E · Adaptive Rate: Elapsed → aktuelles Poll-Intervall (ms).
// Reine Funktion, deterministisch, ohne Nebeneffekte — deterministisch testbar.
export function _pollIntervalFor(elapsedMs) {
  if (elapsedMs < POLL_PHASE_2_START_MS) return POLL_INTERVAL_MS;
  if (elapsedMs < POLL_PHASE_3_START_MS) return POLL_PHASE_2_INTERVAL_MS;
  return POLL_PHASE_3_INTERVAL_MS;
}

function _notify() {
  _version += 1;
  _listeners.forEach((l) => l());
}
function _subscribe(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}
function _getSnapshot() {
  return _version;
}

export function normKey(text) {
  return (text == null ? "" : String(text)).trim().toLowerCase().split(/\s+/).join(" ");
}

// IP2-D1C · defensives Einlesen des additiven Manifestfelds: immer Plain-Map,
// jede andere Form (fehlend/null/Liste/String) wird deterministisch zu {}.
function _asMobileMap(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

// IP2-D1C · Monteur-Renderkontext aktivieren/deaktivieren. Deterministisch,
// ohne Netzwerkverhalten; nur die URL-AUSWAHL in `useLehrbildImage` ändert
// sich (Mobile bevorzugt, sonst Master). Kein try/catch, kein Zweitrequest.
export function setLehrbildMonteurKontext(aktiv) {
  const next = !!aktiv;
  if (next !== _monteurKontext) {
    _monteurKontext = next;
    _notify();
  }
}

// IP2-E1C · Desktop-/Owner-Renderkontext aktivieren/deaktivieren (exakt
// analog zum Monteur-Setter). Boolean-only: keine URLs, keine Bilddaten,
// keine Derivate, keine Maps. In E1C ohne jede Auswirkung auf die
// Bildauswahl — reine Vorbereitung des späteren Umschaltpunkts (E1D).
export function setLehrbildDesktopKontext(aktiv) {
  const next = !!aktiv;
  if (next !== _desktopKontext) {
    _desktopKontext = next;
    _notify();
  }
}

// IP2-D2 · Print-Renderkontext aktivieren/deaktivieren (exakt analog zu
// Monteur-/Desktop-Setter). Boolean-only, deterministisch, ohne Netzwerk-
// verhalten; nur die URL-AUSWAHL in `useLehrbildImage` ändert sich
// (Print bevorzugt, sonst Master). Kein try/catch, kein Zweitrequest.
export function setLehrbildPrintKontext(aktiv) {
  const next = !!aktiv;
  if (next !== _printKontext) {
    _printKontext = next;
    _notify();
  }
}

// BLOCK 3D · Refresh-Fix · Prüft, ob das Manifest einer Welt bereits terminal
// geschrieben wurde (Backend hat `_write_welt_manifest` durchlaufen).
function _isTerminal(worldId) {
  const meta = _manifestMeta[worldId];
  return !!(meta && meta.updated_at);
}

async function _ensureManifest(worldId) {
  if (!worldId || _loaded[worldId]) return;
  _loaded[worldId] = true;
  try {
    const data = await api.getLehrbildManifest(worldId);
    _manifests[worldId] = (data && data.images) || {};
    // IP2-D1C · additives Feld aus IP2-D1B (Altbestand ohne Feld → {}).
    _mobileDerivatives[worldId] = _asMobileMap(data && data.mobile_derivatives);
    // IP2-E1D · additives Feld aus IP2-E1B (Altbestand ohne Feld → {}).
    _webDerivatives[worldId] = _asMobileMap(data && data.web_derivatives);
    // IP2-D2 · additives Feld aus IP2-D2A (Altbestand ohne Feld → {}).
    _printDerivatives[worldId] = _asMobileMap(data && data.print_derivatives);
    _manifestMeta[worldId] = {
      updated_at: (data && data.updated_at) || null,
      count: (data && typeof data.count === "number") ? data.count : 0,
    };
  } catch {
    _manifests[worldId] = {};
    _mobileDerivatives[worldId] = {};
    _webDerivatives[worldId] = {};
    _printDerivatives[worldId] = {};
    _manifestMeta[worldId] = { updated_at: null, count: 0 };
  }
  _notify();
  // BLOCK 3D · Refresh-Fix · Zentraler Poll wird NUR gestartet, wenn (a) das
  // Manifest noch nicht terminal geschrieben ist UND (b) die betroffene Welt
  // aktuell noch die aktive ist. Ein Nutzer, der während des initialen
  // Fetches zu einer anderen Welt gewechselt hat, verursacht so keinen
  // verwaisten Poll. Beim Rückwechsel zu einer noch-nicht-terminalen Welt
  // wird ein neuer Poll erzeugt (siehe `setActiveLehrbildWorld`).
  if (!_isTerminal(worldId) && _activeWorld === worldId) {
    _startCentralPoll(worldId);
  }
}

// BLOCK 3D · Sub-Schritt C · aktives Nachladen (Cache-Bypass) für das
// Manifest-Polling während einer asynchron entstehenden LP3-Preview-Welt.
// Bleibt außerhalb des normalen Abo-Pfads (nur durch `startLehrbildManifestPolling`
// aufgerufen). Fail-closed: bei Fehler wird der letzte bekannte Stand behalten.
//
// BLOCK 3D · Refresh-Fix · Speichert zusätzlich `updated_at`/`count` in
// `_manifestMeta[worldId]`, damit der Poll-Tick den terminalen Zustand
// zuverlässig erkennt (bytefreier Vergleich `updated_at != null`).
async function _reloadManifest(worldId) {
  if (!worldId) return;
  try {
    const data = await api.getLehrbildManifest(worldId);
    _manifests[worldId] = (data && data.images) || {};
    // IP2-D1C · additives Feld aus IP2-D1B (Altbestand ohne Feld → {}).
    _mobileDerivatives[worldId] = _asMobileMap(data && data.mobile_derivatives);
    // IP2-E1D · additives Feld aus IP2-E1B (Altbestand ohne Feld → {}).
    _webDerivatives[worldId] = _asMobileMap(data && data.web_derivatives);
    // IP2-D2 · additives Feld aus IP2-D2A (Altbestand ohne Feld → {}).
    _printDerivatives[worldId] = _asMobileMap(data && data.print_derivatives);
    _manifestMeta[worldId] = {
      updated_at: (data && data.updated_at) || null,
      count: (data && typeof data.count === "number") ? data.count : 0,
    };
    _loaded[worldId] = true;
  } catch {
    // last known state beibehalten (Manifest + Meta)
  }
  _notify();
}

// BLOCK 3D · Refresh-Fix · Interner, consumer-agnostischer Wrapper: startet
// den zentralen Poll ausschließlich unter `updated_at`-Semantik (kein
// `expectedKeys` nötig, weil das Manifest im Backend nachweislich binär
// von „nicht existent" → „vollständig geschrieben" wechselt). Idempotent
// via `_polls[worldId]`-Guard in `startLehrbildManifestPolling`.
function _startCentralPoll(worldId) {
  // BLOCK 3E · adaptive Polling-Rate: KEIN explizites `intervalMs` übergeben,
  // damit `startLehrbildManifestPolling` intern `_pollIntervalFor(elapsed)`
  // verwendet (3 s → 5 s → 10 s). Rückwärtskompatibilität für ausdrücklich
  // externe feste Aufrufe (`WeltDokument.jsx` mit `intervalMs: 3000`) bleibt
  // vollständig erhalten.
  startLehrbildManifestPolling(worldId, [], {
    timeoutMs: POLL_TIMEOUT_MS,
  });
}

export function setActiveLehrbildWorld(worldId) {
  const previous = _activeWorld;
  if (previous !== worldId) {
    // BLOCK 3D · Refresh-Fix · Weltwechsel A→B: bestehender Poll für A wird
    // zentral gestoppt (keine verwaisten Backend-Requests, keine Cross-World-
    // Notifies). `_polls[previous]?.stop` ist idempotent.
    if (previous && _polls[previous]) {
      try { _polls[previous].stop(); } catch { /* fail-soft */ }
    }
    _activeWorld = worldId;
    _notify();
  }
  // BLOCK 3D · Refresh-Fix · Rückwechsel auf eine bereits geladene Welt:
  // `_loaded[worldId]===true`, `_ensureManifest` ist no-op. Falls das
  // Manifest damals noch nicht terminal war, den Poll erneut anstoßen
  // (idempotent, kein Duplikat).
  if (worldId && _loaded[worldId] && !_isTerminal(worldId)) {
    _startCentralPoll(worldId);
  }
  _ensureManifest(worldId);
}

/**
 * BLOCK 3D · Sub-Schritt C · Manifest-Polling für asynchron entstehende
 * LP3-Vorschau-Bilder.
 *
 * Verbindliches Verhalten:
 *   • Adaptives Intervall (Default): 0–30 s → 3 s · 30 s–2 min → 5 s · ab 2 min → 10 s.
 *     Aufrufer mit explizitem `opts.intervalMs` behalten das feste Intervall
 *     (Rückwärtskompatibilität für `WeltDokument.jsx`, Tests, u. a.).
 *   • Timeout (Default): 720.000 ms (12 Min) · anpassbar via `opts.timeoutMs`.
 *   • max. EIN aktiver Poll pro `worldId` (`_polls[worldId]`-Guard)
 *   • BLOCK 3D · Refresh-Fix: Primäres Abschluss-Kriterium ist ein
 *     vorhandenes `updated_at` in der Manifest-Response. Sobald das
 *     Backend `_write_welt_manifest` durchlaufen hat, endet der Poll —
 *     unabhängig davon, ob das Manifest Bilder enthält (`images={}` +
 *     `updated_at` = alle Bilder gescheitert, aber Job terminal).
 *   • Optionales sekundäres Kriterium `expectedKeys` (Rückwärts-
 *     kompatibilität für alte Aufrufer wie `WeltDokument.jsx`): der Poll
 *     endet zusätzlich, sobald ALLE Keys im Manifest aufgelöst sind.
 *   • Polling endet spätestens nach `timeoutMs`.
 *   • Polling endet bei `stop()`-Aufruf (Unmount / Weltwechsel via
 *     `setActiveLehrbildWorld`).
 *   • Polling endet, sobald der Tick erkennt, dass `_activeWorld !== worldId`
 *     (Verwaisen-Schutz nach Weltwechsel, der nach Poll-Registrierung erfolgte).
 *   • kein paralleler Loop, kein SSE, kein WebSocket, kein neues Manifestformat.
 *
 * @param {string} worldId
 * @param {string[]|null|undefined} expectedKeys - optionale Text-Slots
 *        (werden per `normKey` vergleichbar gemacht). Leer/undefined =>
 *        rein `updated_at`-basierter Poll.
 * @param {{intervalMs?: number, timeoutMs?: number}} [opts]
 * @returns {() => void} stop-Funktion (idempotent)
 */
export function startLehrbildManifestPolling(worldId, expectedKeys, opts = {}) {
  if (!worldId) return () => {};
  const list = Array.isArray(expectedKeys) ? expectedKeys : [];
  // BLOCK 3E · Explizites Intervall (falls Aufrufer eines gesetzt hat) hat
  // Vorrang vor der adaptiven Phasen-Strategie. Damit bleiben bestehende
  // Consumer (`WeltDokument.jsx`) und die vorhandenen Poll-Tests
  // byte-verhaltensgleich.
  const explicitIntervalMs =
    typeof opts.intervalMs === "number" && opts.intervalMs > 0
      ? opts.intervalMs
      : null;
  const timeoutMs = opts.timeoutMs || POLL_TIMEOUT_MS;

  // Bereits ein Poll für diese Welt aktiv → keine parallele Schleife.
  if (_polls[worldId]) return _polls[worldId].stop;

  const expected = new Set(
    list.map((t) => normKey(t)).filter((k) => k.length > 0)
  );
  const startedAt = Date.now();

  const stop = () => {
    const p = _polls[worldId];
    if (!p) return;
    clearTimeout(p.id);
    delete _polls[worldId];
  };

  // BLOCK 3D · Refresh-Fix · Wenn das Manifest bereits terminal ist, ist
  // ein Poll sinnlos — sofortiger No-Op-Return.
  if (_isTerminal(worldId)) return stop;

  // BLOCK 3D · Sub-Schritt-C-Rückwärtskompatibilität: falls `expected` gesetzt
  // und bereits vollständig aufgelöst, ist ein Poll ebenfalls sinnlos.
  if (expected.size > 0) {
    const currentMap = _manifests[worldId] || {};
    let allDone = true;
    for (const k of expected) {
      if (!currentMap[k]) { allDone = false; break; }
    }
    if (allDone) return stop;
  }

  // BLOCK 3E · Adaptive Delay-Berechnung. Für explizites Intervall wird der
  // vom Aufrufer vorgegebene Wert genutzt (fest), sonst wählt die
  // Phasen-Funktion die aktuelle Rate.
  const nextDelay = () =>
    explicitIntervalMs != null
      ? explicitIntervalMs
      : _pollIntervalFor(Date.now() - startedAt);

  const tick = async () => {
    if (!_polls[worldId]) return;
    // BLOCK 3D · Refresh-Fix · Verwaisen-Schutz: nach Poll-Registrierung
    // kann der Nutzer die Welt gewechselt haben. In diesem Fall darf der
    // Poll keine unnötigen Backend-Requests mehr abschicken. Ein
    // `_activeWorld === null` bedeutet: es gibt aktuell keine aktive Welt
    // (z. B. wird die Runtime von einem Konsumenten ohne
    // `setActiveLehrbildWorld`-Aufruf angesteuert — Sub-Schritt-C-
    // Legacy-Pfad). In diesem Fall greift der Verwaisen-Schutz NICHT.
    if (_activeWorld !== null && _activeWorld !== worldId) {
      stop();
      return;
    }
    if (Date.now() - startedAt >= timeoutMs) {
      stop();
      return;
    }
    await _reloadManifest(worldId);
    // BLOCK 3D · Refresh-Fix · Primäres Abschluss-Kriterium: Manifest
    // trägt jetzt `updated_at` → Bildjob terminal (unabhängig vom Inhalt).
    if (_isTerminal(worldId)) {
      stop();
      return;
    }
    // Sekundäres Abschluss-Kriterium (Sub-Schritt-C-Kompatibilität):
    // erwartete Slots alle aufgelöst.
    if (expected.size > 0) {
      const map = _manifests[worldId] || {};
      let done = true;
      for (const k of expected) {
        if (!map[k]) { done = false; break; }
      }
      if (done) { stop(); return; }
    }
    // BLOCK 3E · Nächsten Tick planen (adaptiv oder fest).
    scheduleNext();
  };

  const scheduleNext = () => {
    const p = _polls[worldId];
    if (!p) return;
    p.id = setTimeout(tick, nextDelay());
  };

  // Slot registrieren (id wird sofort durch scheduleNext gesetzt).
  _polls[worldId] = { stop, id: null, startedAt, expected };
  scheduleNext();
  return stop;
}

// Nur für Tests: aktuellen Poll-Status abfragen (nicht Teil der öffentlichen API).
export function _debugGetPoll(worldId) {
  return _polls[worldId] || null;
}

// Nur für Tests: aktuelle Manifest-Meta abfragen (nicht Teil der öffentlichen API).
// BLOCK 3D · Refresh-Fix · deterministische Verifikation von `updated_at`-Fluss.
export function _debugGetManifestMeta(worldId) {
  return _manifestMeta[worldId] || null;
}

// Nur für Tests: interne Runtime-Zustände zurücksetzen. Ermöglicht
// deterministische Tests über mehrere `describe`-Blöcke hinweg.
export function _debugResetLehrbildRuntime() {
  Object.keys(_manifests).forEach((k) => delete _manifests[k]);
  Object.keys(_manifestMeta).forEach((k) => delete _manifestMeta[k]);
  Object.keys(_mobileDerivatives).forEach((k) => delete _mobileDerivatives[k]);
  Object.keys(_webDerivatives).forEach((k) => delete _webDerivatives[k]);
  Object.keys(_printDerivatives).forEach((k) => delete _printDerivatives[k]);
  Object.keys(_loaded).forEach((k) => delete _loaded[k]);
  Object.keys(_polls).forEach((k) => {
    try { clearTimeout(_polls[k].id); } catch { /* ignore */ }
    delete _polls[k];
  });
  _activeWorld = null;
  _monteurKontext = false;
  _desktopKontext = false;
  _printKontext = false;
  _version = 0;
  _listeners.clear();
}

// Nur für Tests: Mobile-Derivate einer Welt (nicht Teil der öffentlichen API).
export function _debugGetMobileDerivatives(worldId) {
  return _mobileDerivatives[worldId] || null;
}

// Nur für Tests: Web-Derivate einer Welt (nicht Teil der öffentlichen API).
// IP2-E1D · deterministische Verifikation des reinen Runtime-Einlesens.
export function _debugGetWebDerivatives(worldId) {
  return _webDerivatives[worldId] || null;
}

// Nur für Tests: aktueller Desktop-Kontext (nicht Teil der öffentlichen API).
// IP2-E1C · deterministische Verifikation der reinen Kontext-Vorbereitung.
export function _debugGetLehrbildDesktopKontext() {
  return _desktopKontext;
}

// Nur für Tests: Print-Derivate einer Welt (nicht Teil der öffentlichen API).
// IP2-D2 · deterministische Verifikation des reinen Runtime-Einlesens.
export function _debugGetPrintDerivatives(worldId) {
  return _printDerivatives[worldId] || null;
}

// Nur für Tests: aktueller Print-Kontext (nicht Teil der öffentlichen API).
// IP2-D2 · deterministische Verifikation der Kontext-Umschaltung.
export function _debugGetLehrbildPrintKontext() {
  return _printKontext;
}

export function useLehrbildImage(text, lookupText) {
  useSyncExternalStore(_subscribe, _getSnapshot);
  const map = _manifests[_activeWorld];
  if (!map) return null;
  // AP-LANG-SOURCE: Anzeige bleibt `text` (Mitarbeiter-Sprache), der Manifest-Lookup
  // nutzt den sprachneutralen `lookupText` (deutscher Quelltext). Fehlt er → bisheriges
  // Verhalten (normKey(text)).
  const keySource =
    lookupText != null && String(lookupText).trim() ? lookupText : text;
  const url = map[normKey(keySource)];
  if (!url) return null;
  // IP2-D1C · NUR im Monteur-Kontext: existiert im Manifest ein Mobile-Derivat
  // zum selben Normkey, wird deterministisch die Mobile-URL verwendet —
  // ansonsten automatisch die bisherige Master-URL (kein 404-Erkennen,
  // kein HEAD, kein zweiter Request, kein try/catch).
  if (_monteurKontext) {
    const mobil = (_mobileDerivatives[_activeWorld] || {})[normKey(keySource)];
    if (typeof mobil === "string" && mobil) return `${BACKEND_URL}${mobil}`;
  }
  // IP2-E1D · NUR im Desktop-Kontext: existiert im Manifest ein Web-Derivat
  // zum selben Normkey, wird deterministisch die Web-URL verwendet —
  // ansonsten automatisch die bisherige Master-URL (kein 404-Erkennen,
  // kein HEAD, kein zweiter Request, kein try/catch). AUSWAHLVERTRAG:
  // Monteur+mobile → mobile; sonst Desktop+web → web; sonst Print+print →
  // print; sonst Master (IP2-D2 Prioritätsvertrag).
  if (_desktopKontext) {
    const web = (_webDerivatives[_activeWorld] || {})[normKey(keySource)];
    if (typeof web === "string" && web) return `${BACKEND_URL}${web}`;
  }
  // IP2-D2 · NUR im Print-Kontext: existiert im Manifest ein Print-Derivat
  // zum selben Normkey, wird deterministisch die Print-URL verwendet —
  // ansonsten automatisch die bisherige Master-URL (kein 404-Erkennen,
  // kein HEAD, kein zweiter Request, kein try/catch). Altbestand ohne
  // Print-Derivate bleibt damit vollständig druckfähig (Master-Fallback).
  if (_printKontext) {
    const druck = (_printDerivatives[_activeWorld] || {})[normKey(keySource)];
    if (typeof druck === "string" && druck) return `${BACKEND_URL}${druck}`;
  }
  return `${BACKEND_URL}${url}`;
}

export function LehrbildImage({ text, lookupText, fallback = null, className = "", testId }) {
  const src = useLehrbildImage(text, lookupText);
  if (!src) return fallback;
  return (
    <div className={className} data-testid={testId}>
      <img
        src={src}
        alt={text || ""}
        loading="lazy"
        className="h-full w-full object-contain"
      />
    </div>
  );
}
